import { useMemo, useState } from 'react';
import Button from '@components/common/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/common/Dialog';
import { Input } from '@components/common/Input';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { useCatalog } from '../../../api/hooks/useAnalytics';
import { WidgetRenderer } from './WidgetRenderer';
import type { ChartType, WidgetSpec } from '../../../types/analytics';

interface Props {
  open: boolean;
  initialSpec: WidgetSpec;
  initialTitle: string;
  onClose: () => void;
  onSave: (title: string, spec: WidgetSpec) => void;
}

export function WidgetBuilderModal({ open, initialSpec, initialTitle, onClose, onSave }: Props) {
  const { data: catalog } = useCatalog();
  const [title, setTitle] = useState(initialTitle);
  const [spec, setSpec] = useState<WidgetSpec>(initialSpec);

  const metric = useMemo(
    () => catalog?.metrics.find((m) => m.key === spec.metric),
    [catalog, spec.metric],
  );

  if (!open) return null;

  const setMetric = (key: string) => {
    const m = catalog?.metrics.find((x) => x.key === key);
    setSpec((s) => ({
      ...s, metric: key,
      chart_type: (m?.default_chart_types[0] ?? 'bar') as ChartType,
      dimensions: [], filters: [],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }} size="lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Configure widget</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <Input
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <label className="block text-sm font-medium text-content-secondary">Metric
              <SearchableSelect
                aria-label="Metric"
                className="mt-1"
                options={(catalog?.metrics ?? []).map((m) => ({ id: m.key, label: m.label }))}
                value={spec.metric}
                onChange={setMetric}
                placeholder="Select metric"
                clearable={false}
              />
            </label>
            <label className="block text-sm font-medium text-content-secondary">Chart type
              <SearchableSelect
                aria-label="Chart type"
                className="mt-1"
                options={(metric?.default_chart_types ?? ['bar']).map((chartType) => ({ id: chartType, label: chartType }))}
                value={spec.chart_type}
                onChange={(value) => setSpec((s) => ({ ...s, chart_type: value as ChartType }))}
                placeholder="Chart type"
                clearable={false}
              />
            </label>
            <label className="block text-sm font-medium text-content-secondary">Group by
              <SearchableSelect
                aria-label="Group by"
                className="mt-1"
                options={[
                  { id: '', label: 'None' },
                  ...(metric?.allowed_dimensions ?? []).map((dimension) => ({
                    id: dimension,
                    label: catalog?.dimensions[dimension] ?? dimension,
                  })),
                ]}
                value={spec.dimensions[0] ?? ''}
                onChange={(value) => setSpec((s) => ({ ...s, dimensions: value ? [value] : [] }))}
                placeholder="None"
                clearable={false}
              />
            </label>
            <label className="block text-sm font-medium text-content-secondary">Time range
              <SearchableSelect
                aria-label="Time range"
                className="mt-1"
                options={['last_7d', 'last_30d', 'last_90d', 'last_12m', 'all'].map((preset) => ({ id: preset, label: preset }))}
                value={spec.time_range.preset ?? 'last_30d'}
                onChange={(value) => setSpec((s) => ({ ...s, time_range: { ...s.time_range, preset: value as WidgetSpec['time_range']['preset'] } }))}
                placeholder="Time range"
                clearable={false}
              />
            </label>
          </div>
          <div className="h-64"><WidgetRenderer title={title || 'Preview'} spec={spec} /></div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(title || metric?.label || 'Widget', spec)}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
